import { Component, Input, OnInit } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { DialogComponent } from '../../../shared/dialog/dialog.component';
import { FormUtility } from '../../../shared/forms/form-utility';
import { FormValidation } from '../../../shared/forms/form-validation';

@Component({
  selector: 'claims-app-customer',
  templateUrl: './claims-app-customer.component.html',
})
export class ClaimsAppCustomerComponent implements OnInit {

  public form: UntypedFormGroup;
  public formCompleted = false;
  
  public mbiWarningDialog: MatDialogRef<DialogComponent>;
  private readonly mbiPrefixes = ["MBI", "MBIQ", "MBC"];

  public readonly minCurrentMileage = 0;
  public readonly maxCurrentMileage = 9999999;

  constructor(private dialog: MatDialog) { }

  ngOnInit(): void {
    this.form = new UntypedFormGroup({
      'customerFirstName': new UntypedFormControl("", [Validators.required]),
      'customerLastName': new UntypedFormControl("", [Validators.required]),
      'contractNumber': new UntypedFormControl("", [Validators.required]),
      'vin': new UntypedFormControl("", [Validators.required, Validators.pattern(/^[A-Za-z0-9]{6}$/)])
    });
  }

  /**
   * Formats the contract number input by removing non-alphanumeric characters and capitalizing.
   */
  public onContractNumberInput(): void {
    const rawValue = this.form.controls['contractNumber']?.value;
    
    // Remove all non-alphanumeric characters and capitalize
    if (rawValue) {
      const formattedValue = rawValue.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
      this.form.controls['contractNumber'].setValue(formattedValue);
    }
  }

  /*
   * Enforces contractNumber validation rules when field focus is lost.
   */
  public onContractNumberBlur(): void {
    const controlValue = this.form.controls['contractNumber']?.value;
    if (this.mbiPrefixes.some(p => String(controlValue)?.trim().startsWith(p))) {
      this.showMbiWarningDialog();
    }
  }

  /**
   * Formats the VIN input by removing non-alphanumeric characters and capitalizing.
   * Handles pasted content by removing spaces before applying maxlength restriction.
   */
  public onVinInput(): void {
    const input = this.form.controls['vin']?.value;
    if (input) {
      const formattedValue = input.replace(/[^a-zA-Z0-9]/g, '').toUpperCase().slice(0, 6);
      this.form.controls['vin'].setValue(formattedValue);
    }
  }


  /**
   * Trims leading and trailing spaces from name input fields when focus is lost.
   * @param fieldName The name of the form control to update
   */
  public onNameBlur(fieldName: string): void {
    const control = this.form.controls[fieldName];
    if (control.value) {
      control.setValue(control.value.trim());
    }
  }

  //** Validates the customer form and sets formCompleted to valid or invalid */
  public validateForm(): void {
    this.formCompleted = FormValidation.isValid(this.form);
  }

  /**
   * Resets the form.
   * @see {@link FormUtility.reset}
   */
  public reset(): void {
    FormUtility.reset(this.form);
    this.formCompleted = false;
  }

  /**
   * Clears the value in the specified form field.
   * @param field The form field to clear.
   */
  public clear(field: string): void {
    FormUtility.clear(this.form, field);
  }

  /**
   * Gets a value indicating if the specified field has a value.
   * @param field The form field to check.
   * @returns If the field has a value, `true`; otherwise, `false`.
   */
  public hasValue(field: string): boolean {
    return FormUtility.hasValue(this.form, field)
  }

  public showMbiWarningDialog() {
    this.mbiWarningDialog = this.dialog.open(DialogComponent, {
      data: {
        title: "Claim cannot be submitted online",
        bodyText: "We appreciate your business, unfortunately your claim cannot be submitted online. To proceed with your claim, please contact our support team at 1-800-531-1925 for further assistance. Our representatives are standing by to help you complete your submission.",
        confirmationButtonText: "Close"
      }
    });

    this.mbiWarningDialog.afterClosed().subscribe(_ => {
      // Result does not matter. Always clear the contract number.
      this.form.controls['contractNumber'].setValue('');
    });
  }
}
